import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as tus from 'tus-js-client';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk } from '@/api/queries';
import type { TusUpload } from '@/api/types';
import { UploadsContext, type StartOptions, type UploadItem } from './context';

/** Bunny accepts chunked tus uploads; 50 MB chunks keep retries cheap on flaky connections. */
const CHUNK_SIZE = 50 * 1024 * 1024;

/**
 * Keeps tus uploads alive across navigation. Flow (06 §4.3): POST /videos → tus to the
 * returned endpoint with the signed headers → the video is then polled until ready.
 * Pausing aborts the tus request; resuming fetches fresh credentials (POST /videos/{id}/upload)
 * and continues from the server's offset.
 */
export function UploadsProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const tusRefs = useRef(new Map<string, tus.Upload>());
  const credentials = useRef(new Map<string, TusUpload>());
  const files = useRef(new Map<string, File>());

  const patch = useCallback((key: string, p: Partial<UploadItem>) => {
    setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...p } : u)));
  }, []);

  const runTus = useCallback(
    (key: string, file: File, title: string, upload: TusUpload) => {
      const existing = tusRefs.current.get(key);
      if (existing) {
        existing.options.headers = tusHeaders(upload);
        existing.options.endpoint = upload.endpoint;
        existing.start();
        return;
      }
      const t = new tus.Upload(file, {
        endpoint: upload.endpoint,
        headers: tusHeaders(upload),
        metadata: { filetype: file.type || 'video/mp4', title },
        chunkSize: CHUNK_SIZE,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        removeFingerprintOnSuccess: true,
        onProgress: (sent) => patch(key, { bytesSent: sent }),
        onError: (err) => patch(key, { phase: 'error', error: err.message || 'Upload failed.' }),
        onSuccess: () => {
          patch(key, { phase: 'uploaded', bytesSent: file.size });
          void qc.invalidateQueries({ queryKey: ['videos'] });
          void qc.invalidateQueries({ queryKey: qk.video(key) });
        },
      });
      tusRefs.current.set(key, t);
      t.start();
    },
    [patch, qc],
  );

  const start = useCallback(
    async ({ file, title, purpose, onCreated }: StartOptions) => {
      const tempKey = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const item: UploadItem = {
        key: tempKey,
        videoId: null,
        title,
        fileName: file.name,
        size: file.size,
        purpose,
        phase: 'creating',
        bytesSent: 0,
        error: null,
      };
      setUploads((list) => [item, ...list]);
      try {
        const { video, upload } = await adminApi.createVideo({ title, purpose });
        const key = video.id;
        files.current.set(key, file);
        credentials.current.set(key, upload);
        setUploads((list) =>
          list.map((u) =>
            u.key === tempKey ? { ...u, key, videoId: video.id, phase: 'uploading' } : u,
          ),
        );
        qc.setQueryData(qk.video(video.id), video);
        void qc.invalidateQueries({ queryKey: ['videos'] });
        await onCreated?.(video);
        runTus(key, file, title, upload);
        return video;
      } catch (e) {
        patch(tempKey, { phase: 'error', error: errorMessage(e) });
        return null;
      }
    },
    [patch, qc, runTus],
  );

  const pause = useCallback(
    (key: string) => {
      const t = tusRefs.current.get(key);
      if (!t) return;
      void t.abort();
      patch(key, { phase: 'paused' });
    },
    [patch],
  );

  const resume = useCallback(
    async (key: string) => {
      const file = files.current.get(key);
      const item = uploads.find((u) => u.key === key);
      if (!file || !item?.videoId) return;
      patch(key, { phase: 'uploading', error: null });
      try {
        // Always ask for fresh signed headers: the old ones may have expired (6 h).
        const fresh = await adminApi.renewUpload(item.videoId);
        credentials.current.set(key, fresh);
        runTus(key, file, item.title, fresh);
      } catch (e) {
        patch(key, { phase: 'error', error: errorMessage(e) });
      }
    },
    [patch, runTus, uploads],
  );

  const dismiss = useCallback((key: string) => {
    const t = tusRefs.current.get(key);
    if (t) void t.abort();
    tusRefs.current.delete(key);
    files.current.delete(key);
    credentials.current.delete(key);
    setUploads((list) => list.filter((u) => u.key !== key));
  }, []);

  const value = useMemo(
    () => ({ uploads, start, pause, resume, dismiss }),
    [uploads, start, pause, resume, dismiss],
  );
  return <UploadsContext.Provider value={value}>{children}</UploadsContext.Provider>;
}

/** Bunny's presigned tus headers as plain strings. */
function tusHeaders(upload: TusUpload): Record<string, string> {
  return Object.fromEntries(Object.entries(upload.headers).map(([k, v]) => [k, String(v)]));
}
